import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue, VStack, Text, Link } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { ShopNowCard } from '@/components/ShopNowCard'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import { AsicIcon } from '@/components/icons/AsicIcon'
import { CartsIcon } from '@/components/icons/CartsIcon'
import { CardIcon } from '@/components/icons/CardIcon'
import { CardData, SectionWithCards } from '@/components/SectionWithCards'
import { SUPPORT_PAYMENT_PLAN_FAQ_URL } from '@/constants'
import { HighlightItem } from '@/types/highlights.interface'
import { ISlide } from '@/types/strapi/commonComponents.interface'
import SeoHead from '@/components/seo-head'

const PaymentPlans: NextPage = () => {
  const t = useTranslations()
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')
  const heroBg = useColorModeValue('blackAlpha.50', 'gray.940')
  const cardBg = useColorModeValue('white', 'black')
  const cardTitleColor = useColorModeValue('black', 'white')
  const cardDescriptionColor = useColorModeValue(
    'blackAlpha.800',
    'whiteAlpha.800'
  )
  const cardBorderColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')

  const items: HighlightItem[] = [
    {
      icon: AsicIcon,
      title: t('payment-plan.choose-eligible-hardware'),
      description: t('payment-plan.marked-miners'),
    },
    {
      icon: CartsIcon,
      title: t('payment-plan.select-payment-plan'),
      description: t('payment-plan.choose-payment-plan'),
    },
    {
      icon: CardIcon,
      title: t('payment-plan.manage-pay-time'),
      description: t('payment-plan.manage-installment'),
    },
  ]

  const affordableMining: CardData[] = [
    {
      title: t('payment-plan.increased-accessibility'),
      description: t('payment-plan.lower-initial-barrier'),
      iconImage: { url: '/img/antminer-m30.png' },
    },
    {
      title: t('payment-plan.improved-cash-flow'),
      description: t('payment-plan.maintain-better-control'),
      iconImage: { url: '/img/maximize-returns.png' },
      iconImageMobile: { url: '/img/maximize-returns-mobile.png' },
    },
  ]

  const billingSlideshow: ISlide[] = [
    {
      id: t('payment-plan.associated-costs'),
      title: t('payment-plan.associated-costs'),
      description: t('payment-plan.comprehensive-overview'),
      image: { url: '/img/payment-plan-slide1.gif' },
      imageMobile: { url: '/img/payment-plan-slide1.gif' },
    },
    {
      id: t('payment-plan.payment-schedule'),
      title: t('payment-plan.payment-schedule'),
      description: t('payment-plan.clearly-outlining'),
      image: { url: '/img/payment-plan-page-v2.png' },
      imageMobile: { url: '/img/payment-plan-page-v2.png' },
    },
    {
      id: t('payment-plan.pay-early'),
      title: t('payment-plan.pay-early'),
      description: t('payment-plan.settle-payment'),
      image: { url: '/img/payment-plan-slide2.gif' },
      imageMobile: { url: '/img/payment-plan-slide2.gif' },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('payment-plan.seo.title')}
        breadcrumbTitle={t('payment-plan.seo.breadcrumb')}
        description={t('payment-plan.seo.description')}
      />
      <Box bg={heroBg}>
        <PageHero
          title={t('payment-plan.start-mining-sooner')}
          subTitle={t('common.labels.payment-plans')}
          description={t('payment-plan.spread-hardware-costs')}
          image="img/payment-plan-asic.png"
          mobileImage="img/payment-plan-asic.png"
          anchor="what-is-payment-plan"
        />
      </Box>
      <Box id="what-is-payment-plan">
        <InfoFrame
          title={t('payment-plan.what-is-payment-plan')}
          description={t.rich('payment-plan.flexible-payment-plans', {
            br: () => <br />,
          })}
        />
      </Box>
      <Highlights
        title={t('payment-plan.flexible-payment-options')}
        items={items}
        columns={3}
      />
      <ShopNowCard />
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('payment-plan.make-mining-affordable')}
            subtitle={t('payment-plan.lower-upfront-costs')}
            cards={affordableMining}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg={cardBg}
            cardTitleColor={cardTitleColor}
            cardDescriptionColor={cardDescriptionColor}
            cardBorderColor={cardBorderColor}
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
        </ContainerBase>
      </Box>
      <ContainerBase px={{ base: '32px', md: 4 }}>
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('payment-plan.transparency-flexibility')}
            subtitle={t('payment-plan.easily-review')}
            slideshow={billingSlideshow}
            alignStart
          />
        </Box>
      </ContainerBase>
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '64px', md: '100px' }}
        >
          <VStack gap="24px" textAlign="center">
            <Text
              fontSize={{ base: '24px', md: '32px' }}
              fontFamily="Space Grotesk"
            >
              {t('payment-plan.best-experience')}
            </Text>
            <Text
              fontSize={{ base: '16px', md: '24px' }}
              fontFamily="Space Grotesk"
            >
              {t('payment-plan.payment-options-help')}
            </Text>
            <Link href={SUPPORT_PAYMENT_PLAN_FAQ_URL} color="brand.600">
              {t('payment-plan.payment-plan-faq')}
            </Link>
          </VStack>
        </ContainerBase>
      </Box>
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default PaymentPlans
